import { compress, decompress } from "lz-string";
import {
    IBoutEvent,
    ICollegeEvent,
    ICollegeEventResults,
    IDirectoryTeam,
    IDualMeet_DB,
    IDualMeetBout,
    IExistingFencer,
    IOrganization,
    ITeam,
    ITournament,
    ITournamentEvent,
    ITournamentPool,
    ITournamentRound
} from "../../types";

type CacheOptions =
    | "teams"
    | "fencers"
    | "organizations"
    | "dualMeets"
    | "collegeEvents"
    | "tournaments"
    | "tournamentEvents"
    | "tournamentPools"
    | "tournamentRounds"
    | "teamDirectory"
    | "collegeEventResults";

class CacheService {
    protected readonly PREFIX: string;

    protected queuedSaves: Partial<Record<CacheOptions, ReturnType<typeof setTimeout>>> = {};

    protected save(key: CacheOptions): boolean {
        const value = this[key];
        const str = compress(JSON.stringify(value));
        sessionStorage.setItem(`${this.PREFIX}.${key}`, str);
        return true;
    }

    public queueSave(key: CacheOptions): boolean {
        if (this.queuedSaves[key]) {
            clearTimeout(this.queuedSaves[key]);
        }
        const timeout = setTimeout(() => this.save(key), 1000);
        this.queuedSaves[key] = timeout;
        return true;
    }

    protected loadFromStorage<T>(key: CacheOptions): T | null {
        const ls = sessionStorage.getItem(`${this.PREFIX}.${key}`);
        let result: T | null = null;

        if (ls) {
            let str = ls;
            try {
                str = decompress(str);
            } catch {
                console.error(`Decompress ${this.PREFIX}.${key} failed.`);
            }
            result = JSON.parse(str);
        }

        return result;
    }

    public teamDirectory: { updatedAt: number; directory: Record<string | symbol, IDirectoryTeam> } = { updatedAt: 0, directory: {} };

    public teams: Record<string | symbol, ITeam> = {};
    public fencers: Record<string | symbol, IExistingFencer> = {};
    public organizations: Record<string | symbol, IOrganization> = {};
    public dualMeets: Record<string | symbol, IDualMeet_DB> = {};
    public collegeEvents: Record<string | symbol, ICollegeEvent> = {};
    public tournaments: Record<string | symbol, ITournament> = {};
    public tournamentEvents: Record<string | symbol, ITournamentEvent> = {};
    public tournamentRounds: Record<string | symbol, ITournamentRound> = {};
    public tournamentPools: Record<string | symbol, ITournamentPool> = {};
    public collegeEventResults: Record<string | symbol, ICollegeEventResults> = {};

    public dualMeetBouts: Record<string | symbol, IDualMeetBout> = {};
    public boutEvents: Record<string | symbol, IBoutEvent> = {};

    public constructor(prefix: string) {
        this.PREFIX = prefix;

        this.teamDirectory = this.loadFromStorage<typeof this.teamDirectory>("teamDirectory") || { updatedAt: 0, directory: {} };
        this.fencers = this.loadFromStorage<typeof this.fencers>("fencers") || {};
        this.dualMeets = this.loadFromStorage<typeof this.dualMeets>("dualMeets") || {};
        this.teams = this.loadFromStorage<Record<string, ITeam>>("teams") || {};
        this.organizations = this.loadFromStorage<Record<string, IOrganization>>("organizations") || {};
        this.collegeEvents = this.loadFromStorage<Record<string, ICollegeEvent>>("collegeEvents") || {};
        this.tournaments = this.loadFromStorage<Record<string, ITournament>>("tournaments") || {};
        this.tournamentRounds = this.loadFromStorage<typeof this.tournamentRounds>("tournamentRounds") || {};
        this.tournamentPools = this.loadFromStorage<typeof this.tournamentPools>("tournamentPools") || {};
        this.collegeEventResults = this.loadFromStorage<Record<string, ICollegeEventResults>>("collegeEventResults") || {};
    }
}

export default CacheService;
